import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { ScopeManager } from '../../features/auth/api/scope-manager';
import { AccountHolderSelector } from '../../features/auth/ui/AccountHolderSelector';
import { ScopeContextStorage } from '../../shared/lib/scope-storage';
import { prefetchProductBindings } from '../../features/auth/api/product-binding';
import type { ScopeContext, AccountHolder } from '../../features/auth/model/types';
import { DebugLogger } from '../../shared/lib/debug';
import { isFreshDocumentLoad } from '../../shared/lib/document-navigation';
import { exitToLogin } from '../../shared/lib/session-exit';
import { Logo } from '@/shared/ui/logo';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { useLanguage } from '@/shared/hooks/use-language';

function AccountHolderSelectionPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { t } = useLanguage();
  const sessionId = location.state?.sessionId;
  const [arrivedBackwards] = useState(() => navigationType === 'POP' && !isFreshDocumentLoad());
  const suppressAutoForward = arrivedBackwards || location.state?.reselect === true;

  const [scopeContext, setScopeContext] = useState<ScopeContext>({});
  const [accountHolders, setAccountHolders] = useState<AccountHolder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scopeManager, setScopeManager] = useState<ScopeManager | null>(null);
  const initializationRef = useRef(false);

  useEffect(() => {
    DebugLogger.debug('Account Holder Selection page mounted', { 
      sessionId, 
      locationState: location.state 
    });
    
    // Prevent duplicate initialization in React StrictMode
    if (initializationRef.current) {
      DebugLogger.debug('Initialization already in progress, skipping');
      return;
    }
    
    if (!sessionId) {
      DebugLogger.error('No session ID found in location state', { 
        locationState: location.state,
        pathname: location.pathname 
      });
      setError(t("accountHolder.noSessionId"));
      setIsLoading(false);
      return;
    }

    const storedContext = ScopeContextStorage.getScopeContext(sessionId);
    if (storedContext && !ScopeContextStorage.needsAccountHolderSelection(storedContext) && !suppressAutoForward) {
      DebugLogger.debug('Account holder already selected, redirecting to next step', {
        storedContext
      });

      if (ScopeContextStorage.needsAccountSelection(storedContext)) {
        navigate('/account-selection', {
          state: { sessionId, scopeContext: storedContext },
          replace: true
        });
      } else {
        prefetchProductBindings(sessionId, storedContext);
        navigate('/console', {
          state: { sessionId, scopeContext: storedContext },
          replace: true
        });
      }
      return;
    }

    DebugLogger.debug('Session ID found, initializing scope for account holder selection', { sessionId });
    initializationRef.current = true;
    initializeScope();
  }, [sessionId, navigate, suppressAutoForward]);

  const initializeScope = async () => {
    try {
      DebugLogger.debug('Starting scope initialization for account holder selection', { sessionId });
      setIsLoading(true);
      setError(null);
      
      const scopeManager = new ScopeManager(sessionId);
      const context = await scopeManager.initializeScope();
      
      DebugLogger.debug('Scope initialization completed', { context });
      setScopeContext(context);
      setScopeManager(scopeManager);
      
      const hasAccounts = context.account || scopeManager.getAccounts().length > 0;
      if (!context.accountHolder && hasAccounts) {
        DebugLogger.debug('User has direct account access without holder, navigating to account selection', {
          accountId: context.account?.id,
          accountsCount: scopeManager.getAccounts().length
        });

        const accounts = scopeManager.getAccounts();
        const contextToStore: ScopeContext = {
          ...context,
          account: accounts.length === 1 ? accounts[0] : undefined
        };

        ScopeContextStorage.ensureScopeContext(sessionId, contextToStore);

        navigate('/account-selection', {
          state: { sessionId, scopeContext: contextToStore },
          replace: true
        });
        return;
      }

      if (context.accountHolder && !ScopeContextStorage.needsAccountHolderSelection(context) && !suppressAutoForward) {
        DebugLogger.debug('Account holder auto-selected, checking next step', {
          holderId: context.accountHolder.id,
          holderName: context.accountHolder.name
        });

        if (ScopeContextStorage.needsAccountSelection(context)) {
          ScopeContextStorage.ensureScopeContext(sessionId, context);
          navigate('/account-selection', {
            state: { sessionId, scopeContext: context },
            replace: true
          });
        } else {
          ScopeContextStorage.storeScopeContext(sessionId, context);
          prefetchProductBindings(sessionId, context);
          navigate('/console', {
            state: { sessionId, scopeContext: context },
            replace: true
          });
        }
        return;
      }

      // Get account holders for UI selection (only if manual selection needed)
      DebugLogger.debug('Getting account holders for UI selection');
      setAccountHolders(scopeManager.getAccountHolders());
      
      ScopeContextStorage.ensureScopeContext(sessionId, context);

    } catch (err) {
      DebugLogger.error('Scope initialization failed', { 
        sessionId, 
        error: err instanceof Error ? err.message : 'Unknown error' 
      });
      setError(err instanceof Error ? err.message : 'Failed to initialize scope');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccountHolderSelect = async (holder: AccountHolder) => {
    try {
      setIsLoading(true);
      
      if (!scopeManager) {
        throw new Error('Scope manager not initialized');
      }
      
      const context = await scopeManager.setAccountHolder(holder.id);
      
      DebugLogger.debug('Account holder selected', { 
        holderId: holder.id, 
        holderName: holder.name,
        context 
      });
      
      setScopeContext(context);

      if (ScopeContextStorage.needsAccountSelection(context)) {
        DebugLogger.debug('Account selection needed, navigating to account selection page');
        navigate('/account-selection', {
          state: { sessionId, scopeContext: context }
        });
      } else {
        DebugLogger.debug('Scope complete, navigating to console');
        ScopeContextStorage.storeScopeContext(sessionId!, context);
        prefetchProductBindings(sessionId!, context);
        navigate('/console', {
          state: { sessionId, scopeContext: context }
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to select organization');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background animate-fadeIn p-4">
        <div className="w-full max-w-2xl text-center">
          <Logo className="justify-center mb-4" />
          <p className="text-muted-foreground">{t("accountHolder.loading")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background animate-fadeIn p-4">
        <div className="w-full max-w-2xl">
          <Logo className="justify-center mb-8" />
          <Card className="p-6 border-destructive">
            <p className="text-destructive mb-4">
              <strong>{t("accountHolder.error")}</strong> {error}
            </p>
            <Button variant="outline" onClick={exitToLogin}>
              {t("accountHolder.backToLogin")}
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div>
      <AccountHolderSelector
        accountHolders={accountHolders}
        selectedHolder={scopeContext.accountHolder || null}
        onSelect={handleAccountHolderSelect}
        onExit={exitToLogin}
        isLoading={isLoading}
      />
    </div>
  );
}

export default AccountHolderSelectionPage;
